import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Handshake, Rocket, ShieldCheck } from "lucide-react";
import { Reveal } from "./Reveal";

const VALUES = [
  { icon: Rocket, t: "Inovação", d: "Tecnologia atual aplicada a problemas reais de negócios." },
  { icon: ShieldCheck, t: "Confiança", d: "Transparência e responsabilidade em cada projeto." },
  { icon: Handshake, t: "Proximidade", d: "Acompanhamos você de perto, do briefing à entrega." },
];

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section ref={ref} data-testid="about-section" className="relative overflow-hidden py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
        <Reveal className="relative">
          <div className="relative aspect-square overflow-hidden rounded-[2rem] border border-white/[0.07] bg-black">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(124,255,26,.18),transparent_60%)]" />
            <div className="spin-slow absolute left-1/2 top-1/2 h-[80%] w-[80%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-ws-lime/15" />
            <motion.img style={{ y }} src="/img/logo-lockup.webp" alt="Logo WebStation" loading="lazy" className="logo-blend absolute inset-0 m-auto w-[82%]" />
            <span className="absolute bottom-5 left-5 rounded-full border border-white/10 bg-black/60 px-3 py-1.5 font-mono text-[11px] text-ws-muted backdrop-blur-md">EST. 2020 · BRASIL</span>
          </div>
        </Reveal>
        <div>
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.28em] text-ws-lime">
              <span className="text-ws-muted">[08]</span><span className="h-px w-8 bg-ws-lime/60" />Sobre nós
            </p>
            <h2 data-testid="about-title" className="mt-6 font-display text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
              Web<span className="text-ws-lime">Station</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-base leading-relaxed text-ws-text/90 md:text-lg">
              Desde 2020, a WebStation cria soluções digitais e ajuda negócios a terem uma presença digital profissional.
            </p>
            <p className="mt-4 text-base leading-relaxed text-ws-muted">
              Unimos design, tecnologia e estratégia para transformar necessidades reais em sites, aplicativos e sistemas sob medida — sempre com comunicação clara e foco no que importa para o seu negócio. Você não precisa entender de tecnologia: nós cuidamos disso com você.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-3 sm:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.t} delay={0.15 + i * 0.08} className="group rounded-2xl border border-white/[0.07] bg-ws-card p-5 transition-[border-color] duration-300 hover:border-ws-lime/35">
                <v.icon size={20} className="text-ws-lime transition-transform duration-500 group-hover:-translate-y-1" />
                <h3 className="mt-4 font-body text-sm font-semibold">{v.t}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-ws-muted">{v.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
      <p aria-hidden="true" className="pointer-events-none mt-20 select-none whitespace-nowrap text-center font-display text-[18vw] font-semibold leading-none text-outline-lime opacity-40">
        desde 2020
      </p>
    </section>
  );
};
